import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartOptions,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import type { ChartJSOrUndefined } from 'react-chartjs-2/dist/types';
import styles from './LineGraph.module.css';
import lang from '../../languages/en.json';
import { isMobileDevice, formatKpiAmount, isSmallNonZeroValue, formatSmallChangeValue } from '../../utils/appUtils';
import { LineGraphData, TrendDirection, ValueFormat } from '../../models/LineGraphModels';
import { useAppSelector } from '../../redux/hooks';
import { selectIsMultiStoreEnabled } from '../../redux/UserReducer/userSlice';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
);

// Component-specific constants
const LEGEND_OFFSET = -5;
const CHART_POINT_RADIUS = 4;
const CHART_POINT_HOVER_RADIUS = 6;
const CHART_POINT_BORDER_WIDTH = 2;
const CHART_TOOLTIP_PADDING = 12;
const CHART_TOOLTIP_BODY_FONT_SIZE = 13;
const CHART_TOOLTIP_FONT_SIZE = 14;
const CHART_CURVE_TENSION = 0.4;
const CHART_LINE_WIDTH = 2;
const CHART_TICK_SIZE = 11;
const TARGET_TICK_COUNT = 5;
const STEP_RESIDUAL_SMALL = 1.5;
const STEP_RESIDUAL_MEDIUM = 3;
const STEP_RESIDUAL_LARGE = 7;
const POINT_BORDER_COLOR = '#fff';
const DEFAULT_CHART_COLORS = [
  '#1E88E5', // Current year - Blue
  '#8E24AA', // 1 year prior - Purple
  '#FFB300', // 2 years prior - Gold/Orange
  '#9E9E9E', // Peer group - Grey
];

// Wrapper interface for API responses that include format with data
export interface GraphDataset {
  format: ValueFormat;
  datasets: LineGraphData[];
}

export interface TrendIndicator {
  value: number; // Absolute value change (e.g., 624)
  percentage?: number; // Percentage change (e.g., 7.7)
  direction?: TrendDirection;
  label?: string; // e.g., 'vs last month', 'over last year' (default: 'over last year')
  suffix?: string; // e.g., 'K', 'M' for large numbers
}

export interface ViewLink {
  label: string; // e.g., 'View by Dept', 'View by Merch Class'
  onClick: () => void;
}

export interface LineGraphProps {
  title?: string;
  mainValue?: string | number; // The big price/metric value
  format?: ValueFormat; // Display format for main value and trend indicator (default: 'VAL')
  decimalPlaces?: number; // Number of decimal places for main value, trend, and tooltips
  percentageDecimalPlaces?: number; // Number of decimal places for percentage change in trend indicator (default: 2)
  datasets?: LineGraphData[] | null;
  showLegend?: boolean;
  showGrid?: boolean;
  showPoints?: boolean;
  trendIndicator?: TrendIndicator;
  customColors?: {
    positive?: string;
    negative?: string;
    neutral?: string;
  };
  dateRange?: string; // e.g., 'Oct 21 - Nov 20, 2024'
  timePeriod?: string; // e.g., 'YTD', 'MTD', 'QTD'
  isStacked?: boolean;
  isCurved?: boolean; // Line tension
  viewLinks?: ViewLink[]; // Conditional links like 'View by Dept'
}

const getTrendColor = (
  direction: TrendDirection,
  colors: { positive?: string; negative?: string; neutral?: string },
): string | undefined => {
  switch (direction) {
    case 'positive':
      return colors.positive;
    case 'negative':
      return colors.negative;
    default:
      return colors.neutral;
  }
};

const getTrendIcon = (direction: TrendDirection): JSX.Element | null => {
  switch (direction) {
    case 'positive':
      return <span className={styles.trendIconUp}>▲</span>;
    case 'negative':
      return <span className={styles.trendIconDown}>▼</span>;
    default:
      return null;
  }
};

const getTrendDirectionFromValue = (value: number): TrendDirection => {
  if (value > 0) return 'positive';
  if (value < 0) return 'negative';
  return 'neutral';
};

const formatTrendValue = (
  indicator: TrendIndicator,
  format: ValueFormat,
  decimalPlaces?: number,
  percentageDecimalPlaces?: number,
): string => {
  const { value, percentage, suffix = '' } = indicator;

  const effectiveDecimalPlaces = decimalPlaces ?? 2;
  const effectivePercentageDecimalPlaces = percentageDecimalPlaces ?? 2;

  // Format value with proper negative sign placement
  let valueDisplay = isSmallNonZeroValue(value, effectiveDecimalPlaces)
    ? formatSmallChangeValue(value, format, effectiveDecimalPlaces)
    : formatKpiAmount(value, format, null, effectiveDecimalPlaces);

  // Add suffix if provided
  if (suffix) {
    valueDisplay = `${valueDisplay} ${suffix}`;
  }

  // Add percentage in parentheses if provided
  if (percentage !== undefined) {
    if (isSmallNonZeroValue(percentage, effectivePercentageDecimalPlaces)) {
      valueDisplay += ` (${formatSmallChangeValue(percentage, 'PCT', effectivePercentageDecimalPlaces)})`;
    } else {
      valueDisplay += ` (${percentage < 0 ? '-' : ''}${Math.abs(percentage).toFixed(effectivePercentageDecimalPlaces)}%)`;
    }
  }

  return valueDisplay;
};

const LineGraph: React.FC<LineGraphProps> = ({
  title,
  mainValue,
  format = 'VAL',
  decimalPlaces,
  percentageDecimalPlaces = 1,
  datasets = [],
  showLegend = true,
  showGrid = true,
  showPoints = true,
  trendIndicator,
  customColors = {
    positive: '#43A047',
    negative: '#E53935',
    neutral: '#9E9E9E',
  },
  dateRange,
  timePeriod,
  isStacked = false,
  isCurved = false,
  viewLinks,
}): JSX.Element => {
  const chartRef = useRef<ChartJSOrUndefined<'line'>>(null);
  const [legendLeftPadding, setLegendLeftPadding] = useState(40);
  // Access global state to determine if multi-store features are enabled
  const isMultiStoreEnabled = useAppSelector(selectIsMultiStoreEnabled);
  // Filter out 80% benchmark datasets for multi-store users, as they are not relevant in that context
  const normalizedDatasets = useMemo(() => {
    const all = datasets ?? [];
    if (isMultiStoreEnabled) {
      return all.filter(d => !d.label?.includes(lang.pageContent.percentBenchmark));
    }
    return all;
  }, [datasets, isMultiStoreEnabled]);

  const isMobile = useMemo(() => isMobileDevice(), []); // Memoize mobile check

  // Update legend padding to align with chart area
  useEffect(() => {
    const updateLegendPadding = () => {
      if (chartRef.current?.chartArea) {
        setLegendLeftPadding(chartRef.current.chartArea.left + LEGEND_OFFSET);
      }
    };

    const resizeObserver = new ResizeObserver(updateLegendPadding);
    if (chartRef.current?.canvas) {
      resizeObserver.observe(chartRef.current.canvas);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [normalizedDatasets]);

  const hasChartData = useMemo(() => {
    return normalizedDatasets.some(dataset =>
      dataset?.data?.some(point => point?.value !== null && point?.value !== undefined),
    );
  }, [normalizedDatasets]);

  // Process datasets and apply default colors
  const chartData = useMemo(() => {
    const labels = normalizedDatasets[0]?.data.map(d => d.label) || [];

    const processedDatasets = normalizedDatasets.map((dataset, index) => {
      return {
        label: dataset.label,
        data: dataset.data.map(d => d.value),
        borderColor: dataset.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
        borderWidth: dataset.borderWidth || CHART_LINE_WIDTH,
        borderDash: dataset.label?.includes('Peer Group') ? [5, 5] : (dataset.borderDash || []),
        tension: isCurved ? CHART_CURVE_TENSION : 0,
        pointRadius: showPoints ? CHART_POINT_RADIUS : 0,
        pointHoverRadius: showPoints ? CHART_POINT_HOVER_RADIUS : 0,
        pointBackgroundColor:
          dataset.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length],
        pointBorderColor: POINT_BORDER_COLOR,
        pointBorderWidth: CHART_POINT_BORDER_WIDTH,
      };
    });

    return {
      labels,
      datasets: processedDatasets,
    };
  }, [normalizedDatasets, isCurved, showPoints]);

  // Auto-calculate trend direction if not provided
  const effectiveTrendDirection = useMemo(() => {
    if (!trendIndicator) return undefined;
    return trendIndicator.direction ?? getTrendDirectionFromValue(trendIndicator.value);
  }, [trendIndicator]);

  // Extract time period from title or use default
  const effectiveTimePeriod = useMemo(() => {
    // Explicit prop takes precedence
    if (timePeriod) return timePeriod;

    // Check if title contains a time period qualifier in parentheses at the end
    if (title) {
      const match = /\(([^)]+)\)$/.exec(title);
      if (match) {
        const extracted = match[1];
        // Only extract if it contains common time period patterns
        if (/YTD|MTD|QTD|WTD|Rolling/i.test(extracted)) {
          return extracted;
        }
      }
    }

    return 'YTD'; // Default time period
  }, [timePeriod, title]);

  // Clean title by removing time period if it was extracted from title
  const displayTitle = useMemo(() => {
    if (!title) return undefined;

    // Remove time period qualifier from title if it contains one in parentheses
    const match = /\(([^)]+)\)$/.exec(title);
    if (match && /YTD|MTD|QTD|WTD|Rolling/i.test(match[1])) {
      return title.replace(/\s*\([^)]+\)$/, '').trim();
    }

    return title;
  }, [title]);

  // Calculate Y-axis bounds with tick-aligned buffer
  const yAxisBounds = useMemo(() => {
    const allValues = normalizedDatasets
      .flatMap(normalizedDataset => normalizedDataset.data.map(d => d.value))
      .filter(v => v !== null && v !== undefined);

    if (allValues.length === 0) return { min: undefined, max: undefined };

    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const range = maxValue - minValue;

    // Guard: if all values are identical, let Chart.js auto-scale
    if (range === 0) return { min: undefined, max: undefined, stepSize: undefined };

    // Calculate a nice tick step based on the data range
    // This mimics how Chart.js calculates tick intervals
    const roughStep = range / TARGET_TICK_COUNT; // Aim for ~5 ticks
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const residual = roughStep / magnitude;

    // Round to nice numbers: 1, 2, 5, 10
    let niceStep: number;
    if (residual <= STEP_RESIDUAL_SMALL) {
      niceStep = magnitude;
    } else if (residual <= STEP_RESIDUAL_MEDIUM) {
      niceStep = 2 * magnitude;
    } else if (residual <= STEP_RESIDUAL_LARGE) {
      niceStep = 5 * magnitude;
    } else {
      niceStep = 10 * magnitude;
    }

    // Ensure niceStep is at least the minimum distinguishable increment
    // for Intl.NumberFormat compact notation (1 decimal place per tier).
    // Compact tiers (K, M, B) jump every 3 orders of magnitude, so the
    // smallest visible difference is the tier threshold / 10.
    // e.g., millions tier (1e6) → min step = 1e6 / 10 = 1e5 (100K)
    const maxAbsValue = Math.max(Math.abs(minValue), Math.abs(maxValue));
    if (maxAbsValue >= 1e3) {
      const tierExponent = Math.floor(Math.log10(maxAbsValue));
      const compactTierExponent = tierExponent - (tierExponent % 3);
      const minCompactStep = Math.pow(10, compactTierExponent - 1);
      niceStep = Math.max(niceStep, minCompactStep);
    }

    // Round min down and max up to nearest tick step
    const hasNegativeValues = minValue < 0;
    let min: number;
    if (hasNegativeValues) {
      min = Math.floor(minValue / niceStep) * niceStep - niceStep;
    } else {
      // For positive-only data, floor to tick but don't go negative unless already at 0
      const flooredMin = Math.floor(minValue / niceStep) * niceStep;
      min = flooredMin > 0 ? flooredMin - niceStep : 0;
    }
    const max = Math.ceil(maxValue / niceStep) * niceStep + niceStep;

    return { min, max, stepSize: niceStep };
  }, [normalizedDatasets]);

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          padding: CHART_TOOLTIP_PADDING,
          titleFont: {
            size: CHART_TOOLTIP_FONT_SIZE,
            weight: 'bold',
          },
          bodyFont: {
            size: CHART_TOOLTIP_BODY_FONT_SIZE,
          },
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          displayColors: true,
          callbacks: {
            label: function formatTooltipLabel(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += formatKpiAmount(context.parsed.y, format, null, decimalPlaces);
              }
              return label;
            },
          },
        },
      },
      scales: {
        x: {
          display: true,
          grid: {
            display: showGrid,
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            font: {
              size: CHART_TICK_SIZE,
              family: 'Roboto, sans-serif',
            },
            color: '#000000',
            maxRotation: 0,
            minRotation: 0,
          },
        },
        y: {
          display: true,
          stacked: isStacked,
          min: yAxisBounds.min,
          max: yAxisBounds.max,
          grid: {
            display: showGrid,
            color: 'rgba(0, 0, 0, 0.05)',
          },
          ticks: {
            stepSize: yAxisBounds.stepSize,
            font: {
              size: CHART_TICK_SIZE,
              family: 'Roboto, sans-serif',
            },
            color: '#000000',
            callback: function formatYAxisCompact(value) {
              if (typeof value !== 'number') {
                return value;
              }

              const isNegative = value < 0;
              const absValue = Math.abs(value);
              const formattedValue = new Intl.NumberFormat('en-US', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(absValue);

              switch (format) {
                case 'DOL':
                  // Place negative sign before dollar sign using consistent pattern
                  return isNegative ? `-$${formattedValue}` : `$${formattedValue}`;
                case 'PCT':
                  return isNegative ? `-${formattedValue}%` : `${formattedValue}%`;
                default:
                  return isNegative ? `-${formattedValue}` : formattedValue;
              }
            },
          },
        },
      },
    }),
    [showGrid, isStacked, yAxisBounds, format, decimalPlaces],
  );

  return (
    <div className={styles.lineGraphContainer}>
      <div className={styles.header}>
        <div className={styles.leftSection}>
          {displayTitle && <h3 className={styles.title}>{displayTitle}</h3>}
          {mainValue !== undefined && (
            <div className={styles.mainValue}>
              {formatKpiAmount(mainValue, format, null, decimalPlaces)}
            </div>
          )}
          {trendIndicator && effectiveTrendDirection && (
            <>
              <div
                className={styles.trendIndicator}
                style={{ color: getTrendColor(effectiveTrendDirection, customColors) }}>
                {getTrendIcon(effectiveTrendDirection)}
                <span className={styles.trendValue}>
                  {formatTrendValue(trendIndicator, format, decimalPlaces, percentageDecimalPlaces)}
                </span>
              </div>
              <div className={styles.trendLabel}>
                {trendIndicator.label ?? lang.lineGraph.defaultTrendLabel}
              </div>
            </>
          )}
        </div>
        <div className={styles.rightSection}>
          {dateRange && (
            <div className={styles.dateInfo}>
              <div className={styles.dateRange}>{dateRange}</div>
              <div className={styles.timePeriod}>({effectiveTimePeriod})</div>
            </div>
          )}
          {!isMobile && viewLinks && viewLinks.length > 0 && (
            <div className={styles.viewLinks}>
              {viewLinks.map(link => (
                <button
                  key={link.label}
                  className={styles.viewLink}
                  onClick={link.onClick}
                  type="button">
                  {link.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      <div className={styles.chartWrapper}>
        {hasChartData ? (
          <Line
            ref={chartRef}
            data={chartData}
            options={options}
          />
        ) : (
          <div className={styles.noData}>{lang.lineGraph.noData}</div>
        )}
      </div>
      {showLegend && hasChartData && (
        <div
          className={styles.customLegend}
          style={{ paddingLeft: legendLeftPadding }}>
          {normalizedDatasets.map((dataset, index) => {
            const color =
              dataset.color || DEFAULT_CHART_COLORS[index % DEFAULT_CHART_COLORS.length];
            const isDashed = !!dataset.label?.includes('Peer Group') || !!(dataset.borderDash && dataset.borderDash.length > 0);

            return (
              <div
                key={dataset.label ?? index}
                className={isDashed ? `${styles.legendItem} ${styles.peerGroupLegendItem}` : styles.legendItem}
                style={{ order: isDashed ? 1 : 0 }}>
                {isDashed ? (
                  <span
                    className={styles.legendLine}
                    style={{ borderColor: color, borderStyle: 'dashed' }}
                  />
                ) : (
                  <span
                    className={styles.legendDot}
                    style={{ backgroundColor: color }}
                  />
                )}
                {/* Handle multi-line peer group 80% benchmark label by splitting on \n and rendering each line separately */}
                <span className={styles.legendLabel}>
                  {dataset.label?.split('\n').map((line, i) => (
                    <React.Fragment key={i}>{i > 0 && <br />}{line}</React.Fragment>
                  ))}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LineGraph;
